<template>
  <main id="stock" class="page-container" v-if="$route.name === 'stock'">
    <div class="table-container-stock" id="stock">
      <div class="stocks-toolbar">
        <router-link to="/home/stock/add-item" class="primary-btn">
          + Nuovo articolo
        </router-link>
        <button
          :class="['recent-filter-btn', { active: showOnlyRecent }]"
          @click="showOnlyRecent = !showOnlyRecent"
          title="Mostra solo articoli con eventi nell'ultima settimana"
        >
          <font-awesome-icon :icon="['fas', 'calendar-days']" />
          Ultima settimana
        </button>
      </div>
      <div class="search-container">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Cerca articolo..."
          class="search-box"
          @input="handleSearch"
        />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="clear-search-button"
          title="Cancella ricerca"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
      </div>

      <div class="tbl-wrap">
        <table class="tbl stock-table">
          <thead>
            <tr>
              <th></th>
              <th>Nome articolo</th>
              <th
                class="sortable-th"
                @click="toggleSort('category')"
                title="Ordina per Categoria"
              >
                Categoria
                <span class="sort-indicator">{{
                  sortIndicator("category")
                }}</span>
              </th>
              <!-- <th>Quantità</th> -->
              <th
                class="sortable-th"
                @click="toggleSort('coldRent')"
                title="Ordina per Noleggio a freddo"
              >
                Noleggio a freddo
                <span class="sort-indicator">{{
                  sortIndicator("coldRent")
                }}</span>
              </th>
              <th
                class="sortable-th"
                @click="toggleSort('inUse')"
                title="Ordina per A lavoro"
              >
                A lavoro
                <span class="sort-indicator">{{ sortIndicator("inUse") }}</span>
              </th>
              <th>Magazzino</th>
              <th>Ultimo evento</th>
              <th>Data inserimento</th>
              <th>Scheda Tecnica</th>
              <th class="actions-col"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in paginatedItems" :key="index">
              <tr
                class="item-row"
                :class="{ 'item-row--recent': recentItemIds.has(item.id) }"
              >
                <td class="image-cell">
                  <img
                    v-if="item.image"
                    :src="item.image"
                    alt="Immagine articolo"
                    class="mini-image"
                    loading="lazy"
                    @error="(e) => (e.target.style.visibility = 'hidden')"
                  />
                </td>

                <td>{{ item.itemName }}</td>
                <td>{{ item.category }}</td>
                <!-- <td>{{ item.quantity }}</td> -->
                <td>{{ item.coldRent }}</td>
                <td>{{ item.inUse }}</td>
                <td>{{ item.inWarehouse }}</td>
                <td>{{ formatToLocalDate(item.lastUseDate) }}</td>
                <td>{{ formatToLocalDate(item.entryDate) }}</td>
                <td class="doc-cell">
                  <span
                    v-if="item.documents && item.documents.length > 0"
                    id="open-file"
                    @click="openItemDocumentModal(item)"
                  >
                    <font-awesome-icon :icon="['fas', 'file']" />
                    Apri File
                  </span>

                  <span v-else class="no-doc">
                    Nessuna scheda tecnica presente
                  </span>
                </td>

                <td class="actions-cell">
                  <div
                    v-if="activeQuotesForItem(item.id).length > 0"
                    class="item-quotes-wrapper"
                  >
                    <button
                      class="icons-button"
                      @click="openQuotesModal(item)"
                      aria-label="Preventivi attivi"
                    >
                      <font-awesome-icon :icon="['fas', 'calendar-days']" />
                    </button>
                    <span class="item-quotes-tooltip">
                      {{ activeQuotesForItem(item.id).length }} preventiv{{
                        activeQuotesForItem(item.id).length === 1 ? "o" : "i"
                      }}
                      attiv{{
                        activeQuotesForItem(item.id).length === 1 ? "o" : "i"
                      }}
                    </span>
                  </div>
                  <button
                    class="icons-button"
                    @click="uploadItemDocument(item.id)"
                    title="Carica scheda tecnica (PDF)"
                  >
                    <font-awesome-icon :icon="['fas', 'file-arrow-up']" />
                  </button>
                  <button
                    class="icons-button"
                    @click="goToEditItem(item.id)"
                    title="Modifica articolo in magazzino"
                    aria-label="Modifica articolo in magazzino"
                  >
                    <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                  </button>
                  <button
                    class="icons-button"
                    @click="promptArchive(item)"
                    title="Archivia articolo in magazzino"
                    aria-label="Archivia articolo in magazzino"
                  >
                    <font-awesome-icon :icon="['fas', 'trash-can']" />
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div
          v-if="filteredItems.length === 0 && searchQuery"
          class="no-results"
        >
          <p>Nessun articolo trovato per "{{ searchQuery }}"</p>
        </div>
        <div v-else-if="filteredItems.length === 0" class="no-items">
          <p>Nessun articolo disponibile</p>
        </div>
      </div>
    </div>

    <!-- PAGINAZIONE -->
    <div v-if="showPagination" class="pagination-container">
      <div class="pagination-info">
        Mostrando {{ startItem }}-{{ endItem }} di
        {{ filteredItems.length }} articoli
      </div>

      <div class="pagination-controls">
        <button
          @click="prevPage"
          :disabled="currentPage === 1"
          class="pagination-btn"
        >
          ‹ Precedente
        </button>

        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="{
              'page-btn': true,
              active: page === currentPage,
            }"
          >
            {{ page }}
          </button>

          <span v-if="showEllipsis" class="ellipsis">...</span>
        </div>

        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="pagination-btn"
        >
          Successiva ›
        </button>
      </div>

      <div class="pagination-size">
        <label for="pageSize">Articoli per pagina:</label>
        <select
          id="pageSize"
          v-model="pageSize"
          @change="onPageSizeChange"
          class="page-size-select"
        >
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <!-- ▸ MODALE ARCHIVIAZIONE ARTICOLO -->
    <div v-if="showArchiveModal" class="modal-overlay">
      <div class="modal">
        <button
          class="modal__close icons-button"
          @click="cancelArchive"
          title="Chiudi"
          aria-label="Chiudi"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
        <h3 class="modal__title">Archivia articolo</h3>
        <p class="modal__subtitle">
          Sei sicuro di voler archiviare l’articolo
          <strong>{{ selectedItem.itemName }}</strong
          >?
        </p>
        <div class="modal-actions">
          <button class="primary-btn" @click="confirmArchive">Archivia</button>
          <!-- <button class="secondary-btn" @click="cancelArchive">Annulla</button> -->
        </div>
      </div>
    </div>

    <!-- ▸ MODALE SCHEDA TECNICA -->
    <VueFinalModal
      v-model="showTechSheetModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="documents-modal-content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="closeTechSheetModal">
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>

        <h3 class="modal__title">Scheda tecnica</h3>

        <hr class="modal-hr" />

        <div v-if="!selectedTechSheet" class="no-doc">
          Questo articolo non ha una scheda tecnica.
        </div>

        <div v-else class="techsheet-wrapper">
          <!-- 📄 PREVIEW / LINK PDF -->
          <div class="doc-row">
            <a :href="selectedTechSheet.url" target="_blank" class="card-link">
              <div class="card-icon">
                <font-awesome-icon :icon="['fas', 'file-pdf']" size="2x" />
              </div>
              <div class="card-body">
                <div class="card-title">
                  {{ formatDocName(selectedTechSheet.name) }}
                </div>
              </div>
            </a>

            <!-- 🗑️ Pulsante rimozione -->
            <button
              class="delete-doc-btn"
              @click="deleteTechSheet(selectedTechSheet)"
              title="Elimina scheda tecnica"
            >
              <font-awesome-icon :icon="['fas', 'trash-can']" />
            </button>
          </div>
        </div>
      </template>
    </VueFinalModal>
    <!-- MODALE PREVENTIVI ATTIVI -->
    <div
      v-if="showQuotesModal"
      class="modal-overlay"
      @click.self="showQuotesModal = false"
    >
      <div class="modal item-quotes-modal">
        <button
          class="modal__close icons-button"
          @click="showQuotesModal = false"
          aria-label="Chiudi"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
        <h3 class="modal__title">Preventivi attivi</h3>
        <p class="modal__subtitle">{{ selectedModalItem?.itemName }}</p>
        <hr class="modal-hr" />

        <div class="item-quotes-modal__body">
          <table class="item-quotes-modal__table">
            <thead>
              <tr>
                <th>Evento</th>
                <th>Cliente</th>
                <th>Date noleggio</th>
                <th>Stato</th>
                <th>Qtà</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="q in activeQuotesForItem(selectedModalItem?.id)"
                :key="q.id"
              >
                <td>{{ q.eventName }}</td>
                <td>{{ q.clientName }}</td>
                <td>
                  {{ formatToLocalDate(q.rentStartDate) }} –
                  {{ formatToLocalDate(q.rentEndDate) }}
                </td>
                <td>
                  <span
                    :class="{
                      'state-badge': true,
                      sent: q.state === 'Inviato',
                      accepted: q.state === 'Accettato',
                    }"
                    >{{ q.state }}</span
                  >
                </td>
                <td>{{ q.quantity }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="item-quotes-modal__footer">
          {{ activeQuotesForItem(selectedModalItem?.id).length }} preventiv{{
            activeQuotesForItem(selectedModalItem?.id).length === 1 ? "o" : "i"
          }}
          in corso
        </div>
      </div>
    </div>
  </main>
  <router-view></router-view>
</template>

<script>
import { useToast } from "vue-toast-notification";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import { formatToLocalDate } from "../utilities/formatting";

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapGetters({
      allItems: "items/list",
      allQuotes: "quotes/list",
    }),

    filteredItems() {
      let items = this.allItems;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase().trim();
        items = items.filter((item) => {
          return (
            (item.itemName && item.itemName.toLowerCase().includes(query)) ||
            (item.category && item.category.toLowerCase().includes(query)) ||
            (item.model && item.model.toLowerCase().includes(query)) ||
            (item.id && item.id.toString().includes(query))
          );
        });
      }

      if (this.showOnlyRecent) {
        items = items.filter((item) => this.recentItemIds.has(item.id));
      }

      if (this.sortKey) {
        const dir = this.sortOrder === "asc" ? 1 : -1;
        items = [...items].sort((a, b) => {
          let valA = a[this.sortKey];
          let valB = b[this.sortKey];

          // Gestione stringhe (ignore case)
          if (typeof valA === "string") valA = valA.toLowerCase();
          if (typeof valB === "string") valB = valB.toLowerCase();

          // Fallback per valori null/undefined
          if (valA == null) valA = "";
          if (valB == null) valB = "";

          if (valA < valB) return -dir;
          if (valA > valB) return dir;
          return 0;
        });
      }

      return items;
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.pageSize);
    },

    paginatedItems() {
      // console.log("pageSize", this.pageSize);
      const start = (this.currentPage - 1) * this.pageSize;
      // console.log("start", start);
      const end = start + this.pageSize;
      // console.log("end", end);
      // const filteredItems = this.filteredItems.slice(start, end);
      // console.log("filteredItems", filteredItems);
      return this.filteredItems.slice(start, end);
    },

    showPagination() {
      return this.filteredItems.length > 0;
    },

    startItem() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },

    endItem() {
      const end = this.currentPage * this.pageSize;
      return end > this.filteredItems.length ? this.filteredItems.length : end;
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      // Adjust start if we're near the end
      start = Math.max(1, end - maxVisible + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    showEllipsis() {
      return this.totalPages > this.visiblePages[this.visiblePages.length - 1];
    },

    recentItemIds() {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(0, 0, 0, 0);

      const ids = new Set();
      for (const q of this.allQuotes ?? []) {
        if (q.state === "Chiuso") continue;
        const start = new Date(q.rentStartDate);
        const end = new Date(q.rentEndDate);
        if (start <= today && end >= weekAgo) {
          for (const qi of q.quote_items ?? []) {
            if (qi.item?.id) ids.add(qi.item.id);
          }
        }
      }
      return ids;
    },
  },
  data() {
    return {
      showArchiveModal: false,
      selectedItem: null,
      searchQuery: "",
      currentPage: 1,
      pageSize: 10, // Numero di articoli per pagina: impostato a 10 di default

      showTechSheetModal: false,
      selectedTechSheet: null,
      selectedItemIdForDoc: null,

      showQuotesModal: false,
      selectedModalItem: null,
      sortKey: null, // 'category' | 'coldRent' | 'inUse'
      sortOrder: "asc", // 'asc' | 'desc'
      showOnlyRecent: false,
    };
  },

  watch: {
    // Reset alla prima pagina quando cambia la ricerca
    searchQuery() {
      this.currentPage = 1;
    },

    // Reset alla prima pagina quando cambiano gli items
    allItems() {
      this.currentPage = 1;
    },

    showOnlyRecent() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions([
      "fetchItems",
      "fetchQuotes",
      "archiveItem",
      "uploadItemDocument",
      "deleteItemDocument",
    ]),

    activeQuotesForItem(itemId) {
      if (!itemId) return [];
      return (this.allQuotes ?? [])
        .filter(
          (q) =>
            q.state !== "Chiuso" &&
            Array.isArray(q.quote_items) &&
            q.quote_items.some((qi) => qi.item?.id === itemId)
        )
        .map((q) => {
          const matchingItems = q.quote_items.filter(
            (qi) => qi.item?.id === itemId
          );
          const totalQty = matchingItems.reduce(
            (sum, qi) => sum + qi.quantity,
            0
          );
          return {
            id: q.id,
            eventName: q.eventName,
            clientName: q.Client?.clientName ?? "—",
            rentStartDate: q.rentStartDate,
            rentEndDate: q.rentEndDate,
            state: q.state,
            quantity: totalQty,
          };
        });
    },

    openQuotesModal(item) {
      this.selectedModalItem = item;
      this.showQuotesModal = true;
    },

    // Rimuove “.pdf” dalla fine, lasciando solo il prefisso
    formatDocName(name) {
      return name.replace(/.pdf$/, "");
    },

    formatToLocalDate,

    async getItems() {
      try {
        await this.fetchItems();
      } catch (error) {
        this.$toast.error(
          error.response?.data?.details ||
            "Errore durante il caricamento degli articoli."
        );
      }
    },

    openItemDocumentModal(item) {
      if (!item.documents || item.documents.length === 0) {
        this.selectedTechSheet = null;
        this.showTechSheetModal = true;
        return;
      }

      const doc = item.documents[0];

      // Usa la URL esatta salvata nel DB
      this.selectedTechSheet = {
        name: doc.name,
        url: doc.url,
      };

      this.selectedItemIdForDoc = item.id;

      this.showTechSheetModal = true;
    },

    closeTechSheetModal() {
      this.showTechSheetModal = false;
      this.selectedTechSheet = null;
      this.selectedItemIdForDoc = null;
    },

    async deleteTechSheet(doc) {
      if (!doc || !this.selectedItemIdForDoc) return;

      try {
        const objectName = doc.name;

        await this.$store.dispatch("deleteItemDocument", {
          itemId: this.selectedItemIdForDoc,
          objectName,
        });

        this.$toast.success("Scheda tecnica eliminata con successo.");

        this.closeTechSheetModal();
        await this.fetchItems();
      } catch (err) {
        console.error("Errore eliminazione scheda tecnica:", err);
        this.$toast.error(
          err.response?.data?.error ||
            "Impossibile eliminare la scheda tecnica."
        );
      }
    },

    async uploadItemDocument(itemId) {
      try {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/pdf";

        const file = await new Promise((resolve) => {
          input.onchange = () => resolve(input.files[0]);
          input.click();
        });

        if (!file) return;

        if (file.type !== "application/pdf") {
          this.$toast.info("Puoi caricare solo file PDF.");
          return;
        }

        // Mostra messaggio di elaborazione
        this.$toast.info("Elaborazione del PDF in corso...");

        const arrayBuffer = await file.arrayBuffer();
        const cleanBlob = new Blob([arrayBuffer], { type: "application/pdf" });

        const response = await this.$store.dispatch("uploadItemDocument", {
          itemId,
          document: cleanBlob,
          filename: file.name,
        });

        // Messaggio dinamico basato sulla risposta
        const message =
          response.data?.message || "Scheda tecnica caricata con successo!";
        this.$toast.success(message);

        await this.fetchItems();
      } catch (error) {
        console.error("Errore upload:", error);

        const errorMessage =
          error.response?.data?.details ||
          error.response?.data?.error ||
          "Errore durante il caricamento. Prova con un file PDF diverso.";

        this.$toast.error(errorMessage);
      }
    },

    handleSearch() {
      // La ricerca avviene automaticamente grazie alla computed property filteredItems
      // Puoi aggiungere qui logica aggiuntiva se necessario
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      this.currentPage = 1;
    },

    sortIndicator(key) {
      if (this.sortKey !== key) return "⇅";
      return this.sortOrder === "asc" ? "▲" : "▼";
    },

    clearSearch() {
      this.searchQuery = "";
    },

    // METODI PAGINAZIONE
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.scrollToTop();
      }
      // this.pageSize = 10;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.scrollToTop();
      }
    },

    goToPage(page) {
      this.currentPage = page;
      this.scrollToTop();
    },

    onPageSizeChange() {
      this.currentPage = 1; // Reset alla prima pagina quando cambia la dimensione
    },

    scrollToTop() {
      // SOLUZIONE SICURA: usa document.querySelector invece di this.$el
      const tableContainer = document.querySelector(".tbl-wrap");
      if (tableContainer) {
        tableContainer.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      // ALTERNATIVA: scroll semplice verso l'alto
      // window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    goToEditItem(itemId) {
      this.$router.push({ name: "edit-item", params: { itemId } });
    },
    promptArchive(item) {
      this.selectedItem = item;
      this.showArchiveModal = true;
    },

    async confirmArchive() {
      try {
        const response = await this.archiveItem(this.selectedItem.id);

        if (response.status === 200) {
          this.$toast.success(
            response.data?.message || "Articolo archiviato con successo."
          );
        }
      } catch (err) {
        console.error(err);
        // this.errorMessage =
        this.toast.error(
          err.response?.data?.details ||
            err.response?.data?.error ||
            "Impossibile archiviare l’articolo."
        );
      } finally {
        this.showArchiveModal = false;
        this.selectedItem = null;
        this.getItems();
      }
    },

    cancelArchive() {
      this.showArchiveModal = false;
      this.selectedItem = null;
    },
  },
  async mounted() {
    this.getItems();
    if (!this.allQuotes?.length) {
      await this.fetchQuotes();
    }
  },
};
</script>

<style scoped src="@/css/StockPage.css" />
